// @flow

import React, { Component } from 'react';

import { connect } from 'react-redux';
import { validate } from 'app/redux/modules/dwellings';

import i18n from 'app/common/i18n';
import Button from 'app/common/button';
import Loading from 'app/common/Loading';

import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import Checkbox from 'app/common/form/checkbox';

function mapOwnerDetails(data) {
  try {
    if (data && data.owner) {
      const owner = data.owner;
      return `Ejerforhold: ${owner.ownership['Ejerforhold'] || ''}\nEjere: ${(
        owner.owners || []
      )
        .map((owner) => owner['Ejers navn'] || '(ikke offentligt)')
        .join(', ')}\nAdresse: ${
        owner.details['Beliggenhed'] || ''
      }\nBenyttelse: ${owner.details['Ejendommen benyttes til'] || ''}`;
    }
  } catch (error) {
    if (__DEV__) {
      console.error(error);
    }
  }
  return '';
}

function mapPhoneDetails(data) {
  try {
    if (data && data.phone) {
      const phone = data.phone;
      return `\n${phone.phone || ''}\nNavn: ${phone.name || ''}\nAdresse: ${
        phone.address || ''
      }`;
    }
  } catch (error) {
    if (__DEV__) {
      console.error(error);
    }
  }
  return '';
}

function mapHousingInfo(data) {
  try {
    const ownerDetails = mapOwnerDetails(data);
    const phoneDetails = mapPhoneDetails(data);
    return ownerDetails + phoneDetails;
  } catch (error) {
    if (__DEV__) {
      console.error(error);
    }
  }
  return '';
}

type Props = {
  className?: string,
  style?: Object,
  icon?: any,
  label: string,
  uuid: string,
  validate: Function,
  onApprove: Function,
  onReject: Function
};

type State = {
  open: boolean,
  loading: boolean,
  submitting: boolean,
  data: Object | null,
  reason: string,
  error?: string
};

const mailCheckboxes = [
  {
    label: 'Send godkendelses mail',
    name: 'approval'
  },
  {
    label: 'Send opfølgnings mail',
    name: 'followUp'
  }
];

@connect(() => ({}), { validate })
@i18n
class ValidationDialog extends Component<Props, State> {
  i18n: Function;

  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      loading: false,
      submitting: false,
      error: null,
      data: null,
      reason: '',
      mails: {
        approval: false,
        followUp: true
      }
    };
  }

  handleOpen = () => {
    this.setState({ open: true, loading: true });
    this.props
      .validate(this.props.uuid)
      .then((result) => {
        this.setState({ loading: false, data: result.payload });
      })
      .catch((err) => {
        if (__DEV__) {
          console.error(err);
        }
        this.setState({ loading: false, data: null });
      });
  };

  handleClose = () => {
    this.setState({ open: false, loading: false, data: null, error: null });
  };

  handleCheckboxChange = (ev) => {
    const { name, checked } = ev.currentTarget;
    this.setState((state) => ({ mails: { ...state.mails, [name]: checked } }));
  };

  handleSubmit = (action) => {
    this.setState({ submitting: true }, () => {
      action()
        .catch((err) => {
          if (__DEV__) {
            console.error(err);
          }
        })
        .then(() => this.setState({ submitting: false }));
    });
  };

  handleApprove = () => {
    this.handleSubmit(async () => {
      const reason = `Boligejer info:\n${mapHousingInfo(
        this.state.data
      )}\nKommentar:\n${this.state.reason}`;
      const res = await this.props.onApprove(reason, this.state.mails);
      if (res.error) {
        this.setState({ error: 'Error : Something went wrong..' });
        return;
      }
    });
  };

  handleReject = () => {
    this.handleSubmit(async () => {
      await this.props.onReject(this.state.reason);
      this.handleClose();
    });
  };

  handleChange = (ev: { target: { name: string, value: string } }) => {
    this.setState({ [ev.target.name]: ev.target.value });
  };

  render() {
    const { label, className, style = {}, icon } = this.props;
    const { data, loading, open, reason, error, submitting } = this.state;
    const actions = [
      <Button
        key="close"
        flat
        label={this.props.i18n('Close')}
        onClick={this.handleClose}
      />
    ];

    if (data) {
      actions.push(
        <Button
          key="reject"
          label={this.props.i18n('Reject')}
          secondary
          icon="block"
          disabled={!reason || submitting}
          onClick={this.handleReject}
          style={{ marginLeft: '0.5em' }}
        />,
        <Button
          key="approve"
          label={this.props.i18n('Approve')}
          primary
          icon="done_all"
          disabled={!reason || submitting}
          onClick={this.handleApprove}
          style={{ marginLeft: '0.5em' }}
        />
      );
    }

    return (
      <div className={className} style={style}>
        <Button primary label={label} icon={icon} onClick={this.handleOpen} />
        <Dialog open={open}>
          <DialogTitle>{this.props.i18n('Approve listing')}</DialogTitle>
          <DialogContent>
            {error && <pre style={{ color: 'red' }}>{`${error}`}</pre>}
            {loading ? (
              <Loading />
            ) : data ? (
              <div>
                {data.phone ? (
                  <div
                    style={{
                      paddingBottom: '1em',
                      borderBottom: '2px solid #777',
                      marginBottom: '1em'
                    }}
                  >
                    <strong>
                      <a
                        href={data.phone.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {this.props.i18n('Phone')}
                      </a>
                    </strong>
                    <br />
                    <pre>
                      {[data.phone.phone, data.phone.name, data.phone.address]
                        .filter(Boolean)
                        .join('\n')}
                    </pre>
                    {(data.phone.links || []).filter(Boolean).map((link) => (
                      <div key={link}>
                        <a href={link} target="_blank" rel="noopener noreferrer">
                          {link}
                        </a>
                        <br />
                      </div>
                    ))}
                  </div>
                ) : null}
                {data.owner ? (
                  <div
                    style={{
                      paddingBottom: '1em',
                      borderBottom: '2px solid #777',
                      marginBottom: '1em'
                    }}
                  >
                    <strong>
                      <a
                        href={data.owner.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ejendomsdata
                      </a>
                    </strong>{' '}
                    /{' '}
                    <a
                      href={data.owner.bbrLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Se BBR-meddelse
                    </a>
                    <br />
                    <br />
                    Ejerforhold:{' '}
                    {(data.owner.ownership &&
                      data.owner.ownership['Ejerforhold']) ||
                      ''}
                    <br />
                    Ejere:{' '}
                    {(data.owner.owners || [])
                      .map(
                        (owner) => owner['Ejers navn'] || '(ikke offentligt)'
                      )
                      .join(', ')}
                    <br />
                    Adresse:{' '}
                    {data.owner.details && data.owner.details['Beliggenhed']}
                    <br />
                    Benyttelse:{' '}
                    {data.owner.details &&
                      data.owner.details['Ejendommen benyttes til']}
                    <br />
                  </div>
                ) : null}
                <div
                  style={{
                    paddingBottom: '1em',
                    borderBottom: '2px solid #777',
                    marginBottom: '1em'
                  }}
                >
                  {mailCheckboxes.map((checkbox) => {
                    return (
                      <React.Fragment key={checkbox.name}>
                        <Checkbox
                          {...checkbox}
                          onChange={this.handleCheckboxChange}
                          checked={this.state.mails[checkbox.name]}
                        />
                        <br />
                      </React.Fragment>
                    );
                  })}
                </div>
                <TextField
                  name="reason"
                  helperText={this.props.i18n(
                    'Comments for approval/rejection'
                  )}
                  multiline
                  fullWidth
                  onChange={this.handleChange}
                />
              </div>
            ) : (
              'Something went wrong...'
            )}
          </DialogContent>
          <DialogActions>{actions}</DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default ValidationDialog;